import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Loader2, Search, SearchX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/layout/page-header";
import { RefreshButton } from "@/components/data/refresh-button";
import { DataTable, type Column } from "@/components/data/data-table";
import { EmptyState } from "@/components/data/empty-state";
import { InquiryStatusBadge } from "@/components/data/status-badge";
import {
  INQUIRY_CRITERIA_FIELDS,
  SNAPSHOT_FIELDS,
  inquiriesApi,
  isInquiryActive,
  type InquiryResultRow,
} from "@/api/inquiries";
import { queryKeys } from "@/api/query-keys";
import { TOPE_DETALLE_LOTE, recortaDetalle } from "@/lib/consultas-detalle";
import { formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/consultas/$inquiryId/")({
  component: ConsultaDetallePage,
});

function ConsultaDetallePage() {
  const { inquiryId } = Route.useParams();
  const { t } = useTranslation();

  const consulta = useQuery({
    queryKey: queryKeys.consultas.detail(inquiryId),
    queryFn: () => inquiriesApi.get(inquiryId),
    // Se refresca mientras el bot trabaja y se detiene al terminar. Es lo que hace que
    // el operador vea el resultado aparecer sin recargar.
    // Si el primer fetch falla (corte de red), `data` queda vacío. Sondear igual: no
    // hacerlo congelaba la pantalla para siempre aunque el bot terminara segundos
    // después, y la única salida era que el usuario adivinara que debía refrescar.
    refetchInterval: (query) =>
      !query.state.data || isInquiryActive(query.state.data.status) ? 4_000 : false,
  });

  const data = consulta.data;
  const activa = data ? isInquiryActive(data.status) : false;
  /**
   * ¿El detalle se omitió a propósito? Una consulta en LOTE abre el detalle solo hasta
   * `TOPE_DETALLE_LOTE` filas. Sin esta distinción, la pantalla reportaría "no se pudieron
   * leer los datos" en un caso donde nadie intentó leerlos.
   *
   * El predicado y el tope salen de `lib/consultas-detalle`, los MISMOS que aplica el bot:
   * cuando los dos no coinciden, la pantalla afirma una falla que nunca ocurrió. Pasó con los
   * lotes que entran por la API pública, que traen `source: "api"` y `batchId`, y aquí solo se
   * miraba `layout_upload`.
   *
   * ⚠️ Desde el 2026-09-14 el tope es el tamaño de página del portal y el bot solo lee esa
   * página, así que `resultCount` no puede excederlo y esta rama queda INALCANZABLE. Se
   * conserva a propósito, junto con su texto, para cuando se recorran más páginas.
   */
  const detalleOmitidoPorLote =
    !!data &&
    recortaDetalle(data) &&
    data.resultCount > TOPE_DETALLE_LOTE &&
    // No en una consulta FALLIDA: ahí pudo haber filas parciales sin detalle porque algo se
    // rompió, y decir "no se abrió a propósito" taparía la falla real. La pantalla ya pinta
    // su propia banda de error para ese caso.
    data.status !== "failed";

  const columnas: Column<InquiryResultRow>[] = [
    {
      id: "garantia",
      header: t("consultas.result.garantia"),
      cell: (row) => <span className="font-mono text-sm">{row.numeroGarantia ?? "—"}</span>,
    },
    {
      id: "operacion",
      header: t("consultas.result.operacion"),
      cell: (row) => <span className="font-mono text-sm">{row.numeroOperacion ?? "—"}</span>,
    },
    {
      id: "tipoOperacion",
      header: t("consultas.result.tipoOperacion"),
      cell: (row) => row.tipoOperacion ?? "—",
    },
    {
      id: "otorgante",
      header: t("consultas.result.otorgante"),
      cell: (row) => row.otorganteNombre ?? "—",
    },
    {
      id: "folio",
      header: t("consultas.result.folio"),
      cell: (row) => <span className="font-mono text-sm">{row.folioElectronico ?? "—"}</span>,
    },
    {
      id: "fecha",
      header: t("consultas.result.fecha"),
      cell: (row) => <span className="text-sm">{row.fechaInscripcion ?? "—"}</span>,
    },
    {
      id: "bienes",
      header: t("consultas.result.bienes"),
      cell: (row) => row.tipoBienes ?? "—",
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("consultas.detail.title")}
        subtitle={
          <Link to="/consultas" className="inline-flex items-center gap-1 hover:underline">
            <ArrowLeft className="h-3 w-3" />
            {t("consultas.detail.back")}
          </Link>
        }
        actions={
          <RefreshButton
            onClick={() => void consulta.refetch()}
            spinning={consulta.isFetching}
          />
        }
      />

      {/*
        Consulta inexistente o sin permiso: sin esta rama la página se quedaba
        titulada "Detalle de la consulta" con la tabla en error y sin encabezado, que
        no le dice al operador qué pasó ni cómo salir.
      */}
      {consulta.isError && !data ? (
        <EmptyState
          icon={SearchX}
          title={t("consultas.detail.notFoundTitle")}
          // Texto propio, NO el del backend: `authErrorMessage` cae al `err.message`
          // cuando no reconoce el código, y ahí sale "Inquiry <id> not found", en
          // inglés y con el nombre interno de la entidad. El detalle vive en la consola.
          description={t("consultas.detail.notFoundHelp")}
          actions={
            <Link to="/consultas">
              <Button variant="outline">{t("consultas.detail.back")}</Button>
            </Link>
          }
        />
      ) : null}

      {data ? (
        <div className="space-y-4 rounded-lg border border-border bg-card p-4">
          <div className="flex flex-wrap items-center gap-3">
            <InquiryStatusBadge status={data.status} />
            {activa ? (
              <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                {t("consultas.detail.working")}
              </span>
            ) : null}
            <span className="text-sm text-muted-foreground">
              {formatDateTime(data.createdAt)}
            </span>
            {/* Quién la pidió. Se prefiere el nombre, luego el correo, y solo al final el
                `sub`: es un uuid y no le dice nada a nadie, pero mostrarlo es mejor que
                no decir quién cuando el token no traía los otros claims. Va en el DETALLE
                y no en el listado porque estos campos no están en la proyección de los
                índices, y esa proyección no se puede ampliar sin recrearlos. */}
            {data.createdByName || data.createdByEmail || data.createdBySub ? (
              <span className="text-sm text-muted-foreground">
                {t("consultas.detail.createdBy", {
                  who: data.createdByName || data.createdByEmail || data.createdBySub,
                })}
              </span>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-2">
            {INQUIRY_CRITERIA_FIELDS.filter((k) => data.criteria?.[k]).map((k) => (
              <span
                key={k}
                className="rounded border border-border bg-muted px-2 py-1 text-xs"
              >
                <span className="text-muted-foreground">{t(`consultas.criteria.${k}`)}: </span>
                <span className="font-mono">{data.criteria[k]}</span>
              </span>
            ))}
          </div>

          {/*
            El aviso de truncado es la razón por la que el backend guarda dos números
            distintos. Sin él, ver 20 filas de 988 se leería como "solo existen 20",
            que es una conclusión falsa sobre el registro público.
          */}
          {data.truncated ? (
            <div className="flex items-start gap-2 rounded border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <span>
                {data.totalPortal !== undefined
                  ? t("consultas.detail.truncated", {
                      total: data.totalPortal,
                      // Lo que se PINTA, no lo que dice el contador: si el backend
                      // guardó 20 y serializó 15, decir 20 sería falso.
                      shown: data.results?.length ?? 0,
                    })
                  : // El portal avisó que faltan registros pero no se pudo leer cuántos.
                    // Callar aquí sería peor: dejaría 20 filas pareciendo el total.
                    t("consultas.detail.truncatedUnknown")}
              </span>
            </div>
          ) : null}

          {/* Sin exigir `errorMessage`: un fallo sin mensaje (timeout duro, caída del
              contenedor) dejaba la pantalla sin ninguna señal de que algo salió mal. */}
          {data.status === "failed" ? (
            <div className="rounded border border-destructive/30 bg-destructive/10 p-3 text-sm">
              <span className="font-medium">{t("consultas.detail.failed")}: </span>
              {data.errorMessage ?? t("consultas.detail.failedNoReason")}
            </div>
          ) : null}
        </div>
      ) : null}

      {data ? (
      <DataTable
        data={data?.results ?? []}
        columns={columnas}
        // Los ids vienen del portal y podrían faltar o repetirse; sin el índice, dos
        // filas compartirían llave y expandir una abriría la otra.
        getRowId={(row) =>
          `${row.idGarantia ?? "?"}-${row.idTramite ?? "?"}-${data?.results?.indexOf(row) ?? 0}`
        }
        isLoading={consulta.isLoading}
        isFetching={consulta.isFetching}
        error={consulta.error as Error | null}
        // Cada fila despliega los datos que el bot sacó del detalle de esa garantía.
        renderExpanded={(row) =>
          row.detalle ? (
            <dl className="grid gap-x-6 gap-y-2 p-4 sm:grid-cols-2 lg:grid-cols-3">
              {SNAPSHOT_FIELDS.filter((k) => row.detalle?.[k]).map((k) => (
                <div key={k}>
                  <dt className="text-xs text-muted-foreground">
                    {t(`consultas.snapshot.${k}`)}
                  </dt>
                  <dd className="text-sm">{row.detalle?.[k]}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="p-4 text-sm text-muted-foreground">
              {/* Tres casos distintos, no dos. El criterio es el mismo que ya aplicaba esta
                  pantalla: no afirmar una falla que no ocurrió.

                  1. La consulta sigue corriendo: el detalle todavía no se ha leído.
                  2. Vino de una carga masiva y las filas exceden el tope de página: el bot
                     NO abrió el detalle a propósito. Decir "no se pudo leer" aquí sería
                     reportar un fallo que nadie intentó. Hoy es inalcanzable (ver
                     `detalleOmitidoPorLote`); vive para cuando se recorran más páginas.
                  3. Se intentó y no salió: ahí sí, no se pudo leer. */}
              {activa
                ? t("consultas.detail.snapshotPending")
                : detalleOmitidoPorLote
                  ? t("consultas.detail.snapshotSkippedBatch")
                  : t("consultas.detail.noSnapshot")}
            </p>
          )
        }
        emptyState={
          <EmptyState
            icon={Search}
            // Tres casos distintos, no dos. Decir "el portal no encontró ninguna
            // garantía" cuando la búsqueda FALLÓ es afirmar algo que nunca se
            // comprobó, y es el error más caro que puede cometer esta pantalla.
            title={
              activa
                ? t("consultas.detail.pending")
                : data?.status === "failed"
                  ? t("consultas.detail.failedTitle")
                  : t("consultas.detail.emptyTitle")
            }
            description={
              activa
                ? t("consultas.detail.pendingHelp")
                : data?.status === "failed"
                  ? t("consultas.detail.failedHelp")
                  : t("consultas.detail.emptyHelp")
            }
          />
        }
      />
      ) : null}
    </div>
  );
}
